import { useState, useEffect } from "react";
import { Input, Button, message } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/UserCartContext.jsx";
import useCreateOrder from "../../hooks/useCreateOrder.js";
import { motion } from "framer-motion";

const OrderPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { cart, clearCart, setDrawerVisible } = useCart();
    const { placeOrder, loading, setLoading } = useCreateOrder();
    const [messageApi, contextHolder] = message.useMessage();

    const [customerName, setCustomerName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [phoneError, setPhoneError] = useState(false);
    const [phoneTouched, setPhoneTouched] = useState(false);

    useEffect(() => {
        setDrawerVisible(false);
    }, []);

    const validatePhoneNumber = (value) => /^9\d{9}$/.test(value);

    const handlePhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, "");
        setPhoneNumber(value);
        setPhoneError(!validatePhoneNumber(value));
    };

    const handlePhoneBlur = () => setPhoneTouched(true);

    const getTotalPrice = () =>
        cart.reduce((sum, item) => sum + (item.price || 0) * item.qty, 0);

    const handleSubmit = async () => {
        setLoading(true);

        if (!customerName.trim() || !phoneNumber || phoneError) {
            messageApi.warning(t("unfilledNecessaryFieldsError"));
            setLoading(false);
            return;
        }

        const productsList = cart.map((item) =>
            JSON.stringify({
                qty: item.qty,
                artikul: String(item.artikul || "unknown"),
            })
        );

        const order = {
            phone: phoneNumber,
            name: customerName.trim(),
            products: productsList.join(";"),
        };

        try {
            await placeOrder(order, () => {
                clearCart();
                localStorage.removeItem("cart");
                navigate("/purchased");
            });
        } catch (e) {
            console.error(e);
            messageApi.error(t("orderFailedError"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex justify-center items-center min-h-screen bg-gray-50 px-4">
            {contextHolder}
            <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="w-full max-w-md bg-white p-8 rounded-2xl shadow-2xl"
            >
                <h1 className="text-xl font-bold text-center mb-6 text-gray-800">
                    {t("beforeOrder")}
                </h1>

                <div className="space-y-5">
                    <Input
                        id="customer-name"
                        size="large"
                        placeholder={t("enterFCs")}
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                    />

                    <Input
                        id="phone-number"
                        rootClassName="mt-5"
                        addonBefore="+7"
                        size="large"
                        placeholder={t("enterPhone")}
                        maxLength={10}
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                        onBlur={handlePhoneBlur}
                        status={phoneTouched && phoneError ? "error" : ""}
                    />

                    <div className="text-center text-xl font-semibold text-gray-700 mt-4">
                        {t("total")}: <span className="text-[#0da98c]">{getTotalPrice()} ₽</span>
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        loading={loading}
                        onClick={handleSubmit}
                        className="w-full"
                        style={{
                            backgroundColor: "#0da98c",
                            borderColor: "#0da98c",
                            color: "white",
                            fontSize: "1rem",
                            borderRadius: "0.75rem",
                            height: "48px",
                        }}
                    >
                        {t("confirmOrder")}
                    </Button>
                </div>
            </motion.div>
        </div>
    );
};

export default OrderPage;